/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ChevronRight } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'

import useT from '@/i18n/useT'
import CopyableIdField from './CopyableIdField'

type CurrentImageCardProps = {
  imageId: string
  title?: string
  label?: string
  loadingImage?: boolean
  loadingNext?: boolean
  showNextImageAction?: boolean
  onNextImage?: () => void
}

function CurrentImageCard({
  imageId,
  title,
  label,
  loadingImage = false,
  loadingNext = false,
  showNextImageAction = true,
  onNextImage,
}: CurrentImageCardProps) {
  const { t } = useT()

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6">
                {title ??
                  t('detectionSidebar.currentImage.title', 'Current Image')}
              </Typography>
            </Stack>
            {showNextImageAction && onNextImage ? (
              <Button
                size="small"
                variant="outlined"
                endIcon={<ChevronRight fontSize="small" />}
                onClick={onNextImage}
                disabled={loadingNext || loadingImage}
              >
                {t('detectionSidebar.currentImage.nextImage', 'Next image')}
              </Button>
            ) : null}
          </Stack>
          <Box
            sx={theme => ({
              borderRadius: 2,
              border: '1px solid',
              borderColor: theme.palette.divider,
              px: 1.5,
              py: 1.25,
              backgroundColor: theme.palette.background.default,
            })}
          >
            <CopyableIdField
              label={
                label ?? t('detectionSidebar.currentImage.imageId', 'Image ID')
              }
              value={imageId}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default CurrentImageCard
